import React, { FC, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import DatePicker, { registerLocale } from 'react-datepicker';
import moment from 'moment';
import _ from 'lodash';
import { getYear } from 'date-fns';
import { useTranslation } from 'react-i18next';
import de from 'date-fns/locale/de';
import ca from 'date-fns/locale/ca';
import en from 'date-fns/locale/en-US';
import eu from 'date-fns/locale/eu';
import gl from 'date-fns/locale/gl';
import es from 'date-fns/locale/es';
import { useAppStore } from '../store/AppContext';
import { IconCalendario } from '../Icons';

type Props = {
  type: string;
  doAction: (arg?: any) => void;
  defaultDate?: string;
  control?: any;
  isInvalidDate?: boolean;
  labelDefaultDate?: any;
  startDate?: any;
  setStartDate?: (startDate: any) => void;
  endDate?: any;
  setEndDate?: (startDate: any) => void;
  load?: boolean;
  isSubmitted?: boolean;
  showErrors?: (arg: string) => boolean;
};

const EroskiDatePicker: FC<Props> = ({
  type,
  doAction,
  defaultDate,
  control,
  isInvalidDate,
  labelDefaultDate,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  load,
  isSubmitted,
  showErrors,
}) => {
  const { t } = useTranslation();
  const dataApp = useAppStore();
  const years = _.range(1901, getYear(new Date()) + 1, 1).reverse();
  const [showYears, setShowYears] = useState<boolean>(false);

  useEffect(() => {
    switch (dataApp.lang) {
      case 'de':
        registerLocale('lang', de);
        moment.locale('de');
        break;
      case 'ca':
        registerLocale('lang', ca);
        moment.locale('ca');
        break;
      case 'en':
        registerLocale('lang', en);
        moment.locale('en');
        break;
      case 'eu':
        registerLocale('lang', eu);
        moment.locale('eu');
        break;
      case 'gl':
        registerLocale('lang', gl);
        moment.locale('gl');
        break;
      default:
        registerLocale('lang', es);
        moment.locale('es');
    }
  }, [dataApp.lang]);

  const handleChangeStart = (selectedDate: Date) => {
    if (setStartDate) {
      setStartDate(selectedDate);
    }
  };

  const handleChangeEnd = (selectedDate: Date) => {
    if (setEndDate) {
      setEndDate(selectedDate);
    }
  };

  useEffect(() => {
    if (endDate !== null && startDate !== null && startDate <= endDate) {
      doAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate, startDate]);

  if (type === 'range') {
    return (
      <>
        <div className="form-group u-margin-right-medium">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="control-label">
            {t('components.date-picker.fromDate')}
          </label>
          <div className="form-control-block has-icon datepicker-block">
            <DatePicker
              id="startDate"
              className="form-control has-datepicker"
              selected={startDate}
              placeholderText="DD/MM/YYYY"
              name="startDate"
              onChange={handleChangeStart}
              locale="lang"
              dateFormat="dd/MM/yyyy"
              maxDate={endDate !== null ? endDate : new Date()}
              shouldCloseOnSelect
              showYearDropdown
              yearDropdownItemNumber={40}
              scrollableYearDropdown
              onKeyDown={(e) => e.preventDefault()}
              renderCustomHeader={({
                date,
                changeYear,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingBottom: '4px',
                    paddingTop: '3px',
                  }}
                >
                  <button
                    type="button"
                    className="react-datepicker__navigation react-datepicker__navigation--previous"
                    aria-label="Previous Month"
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                  >
                    Previous Month
                  </button>

                  <div
                    className="react-datepicker__year-dropdown-container react-datepicker__year-dropdown-container--scroll"
                    style={{ marginRight: '20' }}
                  >
                    <div
                      className="react-datepicker__year-dropdown react-datepicker__year-dropdown--scrollable"
                      style={{
                        visibility: showYears ? 'visible' : 'hidden',
                      }}
                    >
                      {years.map((option) => {
                        if (option === moment(date).year()) {
                          return (
                            <div className="react-datepicker__year-option react-datepicker__year-option--selected_year">
                              <span className="react-datepicker__year-option--selected">
                                ✓
                              </span>
                              {option}
                            </div>
                          );
                        }
                        return (
                          // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                          <div
                            className="react-datepicker__year-option"
                            key={option}
                            onClick={() => {
                              changeYear(Number(option));
                              setShowYears(false);
                            }}
                          >
                            {option}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="react-datepicker__current-month react-datepicker__current-month--hasYearDropdown">
                    {moment(date).format('MMMM')}
                  </div>
                  <button
                    type="button"
                    className="react-datepicker__navigation react-datepicker__navigation--next"
                    aria-label="Next Month"
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                  >
                    Next Month
                  </button>
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                  <div
                    className="react-datepicker__year-read-view"
                    style={{ visibility: 'visible' }}
                    onClick={() => setShowYears(!showYears)}
                  >
                    <span className="react-datepicker__year-read-view--down-arrow" />
                    <span className="react-datepicker__year-read-view--selected-year">
                      {moment(date).format('YYYY')}
                    </span>
                  </div>
                </div>
              )}
            />
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <div
              className="icon icon-medium"
              style={{ right: 25 }}
              onClick={() =>
                (document.getElementById('startDate') as HTMLElement).click()
              }
            >
              <IconCalendario />
            </div>{' '}
          </div>
        </div>
        <div className="form-group u-margin-lef-medium">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="control-label">
            {t('components.date-picker.toDate')}
          </label>
          <div className="form-control-block has-icon datepicker-block">
            <DatePicker
              id="endDate"
              className="form-control has-datepicker"
              placeholderText="DD/MM/YYYY"
              name="endDate"
              onChange={handleChangeEnd}
              locale="lang"
              dateFormat="dd/MM/yyyy"
              maxDate={new Date()}
              minDate={startDate !== null ? startDate : new Date()}
              shouldCloseOnSelect
              showYearDropdown
              yearDropdownItemNumber={40}
              scrollableYearDropdown
              startDate={endDate}
              selected={endDate}
              onKeyDown={(e) => e.preventDefault()}
              renderCustomHeader={({
                date,
                changeYear,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingBottom: '4px',
                    paddingTop: '3px',
                  }}
                >
                  <button
                    type="button"
                    className="react-datepicker__navigation react-datepicker__navigation--previous"
                    aria-label="Previous Month"
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                  >
                    Previous Month
                  </button>

                  <div
                    className="react-datepicker__year-dropdown-container react-datepicker__year-dropdown-container--scroll"
                    style={{ marginRight: '20' }}
                  >
                    <div
                      className="react-datepicker__year-dropdown react-datepicker__year-dropdown--scrollable"
                      style={{
                        visibility: showYears ? 'visible' : 'hidden',
                      }}
                    >
                      {years.map((option) => {
                        if (option === moment(date).year()) {
                          return (
                            <div className="react-datepicker__year-option react-datepicker__year-option--selected_year">
                              <span className="react-datepicker__year-option--selected">
                                ✓
                              </span>
                              {option}
                            </div>
                          );
                        }
                        return (
                          // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                          <div
                            className="react-datepicker__year-option"
                            key={option}
                            onClick={() => {
                              changeYear(Number(option));
                              setShowYears(false);
                            }}
                          >
                            {option}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="react-datepicker__current-month react-datepicker__current-month--hasYearDropdown">
                    {moment(date).format('MMMM')}
                  </div>
                  <button
                    type="button"
                    className="react-datepicker__navigation react-datepicker__navigation--next"
                    aria-label="Next Month"
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                  >
                    Next Month
                  </button>
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                  <div
                    className="react-datepicker__year-read-view"
                    style={{ visibility: 'visible' }}
                    onClick={() => setShowYears(!showYears)}
                  >
                    <span className="react-datepicker__year-read-view--down-arrow" />
                    <span className="react-datepicker__year-read-view--selected-year">
                      {moment(date).format('yyyy')}
                    </span>
                  </div>
                </div>
              )}
            />
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <div
              className="icon icon-medium"
              style={{ right: 25 }}
              onClick={() =>
                (document.getElementById('endDate') as HTMLElement).click()
              }
            >
              <IconCalendario />
            </div>{' '}
          </div>
        </div>
      </>
    );
  }

  // type === 'default-date'
  return (
    <>
      <div className="form-group u-margin-bottom">
        <label htmlFor="defaultDate" className="control-label">
          {labelDefaultDate}
        </label>
        <div className="form-control-block has-icon datepicker-block">
          <Controller
            name="defaultDateControl"
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                id={`default-date-${labelDefaultDate}`}
                className={`form-control has-datepicker ${
                  isInvalidDate ? 'error' : ''
                }`}
                value={moment(defaultDate, 'YYYY-MM-DD').format('DD/MM/YYYY')}
                name="defaultDate"
                onChange={(date) => {
                  field.onChange(date);
                  doAction(date);
                }}
                locale="lang"
                dateFormat="dd/MM/yyyy"
                maxDate={endDate !== null ? endDate : new Date()}
                minDate={new Date('01/01/1901')}
                shouldCloseOnSelect
                showYearDropdown
                yearDropdownItemNumber={40}
                scrollableYearDropdown
                openToDate={defaultDate ? new Date(defaultDate) : new Date()}
                startDate={defaultDate ? new Date(defaultDate) : new Date()}
                selected={defaultDate ? new Date(defaultDate) : new Date()}
                onKeyDown={(e) => e.preventDefault()}
                renderCustomHeader={({
                  date,
                  changeYear,
                  decreaseMonth,
                  increaseMonth,
                  prevMonthButtonDisabled,
                  nextMonthButtonDisabled,
                }) => (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      paddingBottom: '4px',
                      paddingTop: '3px',
                    }}
                  >
                    <button
                      type="button"
                      className={`${
                        moment(date).year() === 1901 &&
                        moment(date).month() === 0
                          ? 'hide'
                          : ''
                      } react-datepicker__navigation react-datepicker__navigation--previous`}
                      aria-label="Previous Month"
                      onClick={decreaseMonth}
                      disabled={prevMonthButtonDisabled}
                    >
                      Previous Month
                    </button>

                    <div
                      className="react-datepicker__year-dropdown-container react-datepicker__year-dropdown-container--scroll"
                      style={{ marginRight: '20' }}
                    >
                      <div
                        className="react-datepicker__year-dropdown react-datepicker__year-dropdown--scrollable"
                        style={{
                          visibility: showYears ? 'visible' : 'hidden',
                        }}
                      >
                        {years.map((option) => {
                          if (option === moment(date).year()) {
                            return (
                              <div className="react-datepicker__year-option react-datepicker__year-option--selected_year">
                                <span className="react-datepicker__year-option--selected">
                                  ✓
                                </span>
                                {option}
                              </div>
                            );
                          }
                          return (
                            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                            <div
                              className="react-datepicker__year-option"
                              key={option}
                              onClick={() => {
                                changeYear(Number(option));
                                setShowYears(false);
                              }}
                            >
                              {option}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="react-datepicker__current-month react-datepicker__current-month--hasYearDropdown">
                      {moment(date).format('MMMM')}
                    </div>
                    <button
                      type="button"
                      className="react-datepicker__navigation react-datepicker__navigation--next"
                      aria-label="Next Month"
                      onClick={increaseMonth}
                      disabled={nextMonthButtonDisabled}
                    >
                      Next Month
                    </button>
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                    <div
                      className="react-datepicker__year-read-view"
                      style={{ visibility: 'visible' }}
                      onClick={() => {
                        setShowYears(!showYears);
                      }}
                    >
                      <span className="react-datepicker__year-read-view--down-arrow" />
                      <span className="react-datepicker__year-read-view--selected-year">
                        {moment(date).format('YYYY')}
                      </span>
                    </div>
                  </div>
                )}
              />
            )}
          />
          {(isInvalidDate ||
            (isSubmitted && showErrors?.('fechaNacimiento'))) && (
            <label
              id="defaultDate-error"
              className="error"
              htmlFor={`default-date-${labelDefaultDate}`}
            >
              {t('validaciones.not-legal-age')}
            </label>
          )}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
          <div
            className="icon icon-medium"
            style={{ right: 25 }}
            onClick={() => {
              if (load)
                (document.getElementById(
                  `default-date-${labelDefaultDate}`,
                ) as HTMLElement).click();
            }}
          >
            <IconCalendario />
          </div>
        </div>
      </div>
    </>
  );
};

export default EroskiDatePicker;
